import { useEffect, useRef, useState } from 'react'

/**
 * usePullToRefresh — swipe down from top of page shows a visual indicator
 * and triggers a refresh callback (or falls back to window.location.reload).
 * Only activates on mobile (touch devices). Ignores when already scrolled down.
 */

const THRESHOLD = 80   // px of downward swipe to trigger refresh
const MAX_PULL = 72    // px cap on indicator travel

export interface PullState {
  pullY: number        // 0..MAX_PULL during gesture, 0 otherwise
  isRefreshing: boolean
}

export function usePullToRefresh(onRefresh?: () => void | Promise<void>): PullState {
  const startY = useRef(0)
  const startX = useRef(0)
  const pulling = useRef(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  const [pullY, setPullY] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const isRefreshingRef = useRef(false)

  useEffect(() => {
    if (window.innerWidth >= 768) return

    const onTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current) return
      const target = e.touches[0].target as Element | null

      // Don't activate in overlays or portalled content
      if (target?.closest?.("[role='dialog']") || target?.closest?.(".SlideOver") || target?.closest?.("[data-portal='true']")) {
        pulling.current = false
        return
      }

      // Also check if inside any scrollable container (e.g. popover with overflow:auto)
      let el: Element | null = target
      while (el && el !== document.documentElement) {
        const style = window.getComputedStyle(el)
        const overflow = style.overflowY
        if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight) {
          pulling.current = false
          return
        }
        el = el.parentElement
      }

      startY.current = e.touches[0].clientY
      startX.current = e.touches[0].clientX
      pulling.current = window.scrollY === 0
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!pulling.current) return
      const dy = e.touches[0].clientY - startY.current
      const dx = Math.abs(e.touches[0].clientX - startX.current)
      if (dy > 0 && dx < 40) {
        setPullY(Math.min(dy, MAX_PULL))
      } else {
        if (dy <= 0) { pulling.current = false }
        setPullY(0)
      }
    }

    const onTouchEnd = async (e: TouchEvent) => {
      if (!pulling.current) return
      const dy = e.changedTouches[0].clientY - startY.current
      const dx = Math.abs(e.changedTouches[0].clientX - startX.current)
      pulling.current = false
      setPullY(0)

      if (dy > THRESHOLD && dx < 40) {
        if (onRefreshRef.current) {
          isRefreshingRef.current = true
          setIsRefreshing(true)
          try { await onRefreshRef.current() } finally {
            isRefreshingRef.current = false
            setIsRefreshing(false)
          }
        } else {
          window.location.reload()
        }
      }
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: true })
    document.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }, [])

  return { pullY, isRefreshing }
}
